/* istanbul ignore file */

import {
  createEventCallbackRedirect,
  EVENT_TIMEOUT_IN_MS,
  sendGAEvent,
} from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';
import { RecommendedProduct } from '@noths/polaris-dev-ts-types';

import { Placement, PlacementEntry } from 'src/constants/recommendations';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { getSectionVisibleActionType } from 'src/redux/visibilityTracking';
import { generateProductsForEvent } from 'src/utils';
import { ProductImpressionsInitialEvent } from '../../RecommendedProductsShelf/tracking/events';
import { actions, contactPartnerSubmit, submitMarketingPreferences } from '../modules';
import {
  ContactSellerCloseModal,
  ContactSellerFieldValidationError,
  ContactSellerOpenModal,
  ContactSellerSubmit,
  InitialRelatedProductClickEvent,
  MARKETING_PREFERENCES_VISIBILITY_NAME,
  MarketingPreferencesImpression,
  MarketingPreferencesSubmit,
  PARTNER_STOREFRONT_VISIBILITY_NAME,
  PartnerProductCardsVisible,
  PartnerStorefrontImpression,
  PartnerStorefrontLinkClick,
  PartnerStorefrontNameClick,
  RelatedProductClick,
} from './events';

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [getSectionVisibleActionType(PARTNER_STOREFRONT_VISIBILITY_NAME)]: () => {
    sendGAEvent<PartnerStorefrontImpression>({
      event: 'custom_event',
      event_category: 'Seller shelf',
      event_action: 'Impression',
      event_label: 'not set',
    });
  },
  [actions.openContactPartnerModal.type]: ({ product }) => {
    sendGAEvent<ContactSellerOpenModal>({
      event: 'custom_event',
      event_action: 'Click',
      event_category: 'Contact seller',
      event_label: product.partner.name,
    });
  },
  [actions.closeContactPartnerModal.type]: ({ product }) => {
    sendGAEvent<ContactSellerCloseModal>({
      event: 'custom_event',
      event_action: 'Close',
      event_category: 'Contact seller',
      event_label: product.partner.name,
    });
  },
  [contactPartnerSubmit.fulfilled.type]: ({ product }) => {
    sendGAEvent<ContactSellerSubmit>({
      event: 'custom_event',
      event_action: 'Submit',
      event_category: 'Contact seller',
      event_label: product.partner.name,
    });
  },
  [submitMarketingPreferences.fulfilled.type]: (_, __, action) => {
    const {
      meta: {
        arg: { email },
      },
    } = action as ReturnType<typeof submitMarketingPreferences.fulfilled>;

    sendGAEvent<MarketingPreferencesSubmit>({
      event: 'custom_event',
      event_category: 'Marketing preferences',
      event_action: 'Subscribe',
      event_label: 'Contact seller',
      user_email: email,
    });
  },
  [actions.contactSellerFieldValidationError.type]: (_, __, action) => {
    const { payload } = action as ReturnType<typeof actions.contactSellerFieldValidationError>;

    sendGAEvent<ContactSellerFieldValidationError>({
      event: 'custom_event',
      event_action: 'Error',
      event_category: 'Contact seller',
      event_label: payload,
    });
  },
  [actions.onPartnerStorefrontLinkClick.type]: (_, __, action) => {
    const { payload } = action as ReturnType<typeof actions.onPartnerStorefrontLinkClick>;

    if (payload.event_label === 'Partner name' && payload.useEventCallback) {
      sendGAEvent<PartnerStorefrontNameClick>({
        eventCallback: createEventCallbackRedirect(payload.destination_URL),
        eventTimeout: EVENT_TIMEOUT_IN_MS,
        event: 'custom_event',
        event_action: 'Partner storefront click',
        event_category: 'Seller shelf',
        event_label: payload.event_label,
        destination_URL: payload.destination_URL,
      });
    } else {
      sendGAEvent<PartnerStorefrontLinkClick>({
        event: 'custom_event',
        event_action: 'Partner storefront click',
        event_category: 'Seller shelf',
        event_label: payload.event_label,
        destination_URL: payload.destination_URL,
      });
    }
  },
  [actions.partnerProductCardsVisible.type]: (prevState, __, action) => {
    const {
      payload: { products },
    } = action as ReturnType<typeof actions.partnerProductCardsVisible>;
    const { placementStrategy = '' }: PlacementEntry<RecommendedProduct> =
      prevState.richRelevance[Placement.SameSeller];

    const eventLabel = `${placementStrategy || ''} : ${Placement.SameSeller}`;

    sendGAEvent<ProductImpressionsInitialEvent>({
      ecommerce: null,
    });

    sendGAEvent<PartnerProductCardsVisible>({
      event: 'product_impressions',
      event_category: 'Rich Relevance',
      event_action: 'Product Impressions',
      event_label: eventLabel,
      ecommerce: {
        impressions: generateProductsForEvent({
          products,
          placementName: Placement.SameSeller,
          placementStrategy,
        }),
      },
    });
  },
  [getSectionVisibleActionType(MARKETING_PREFERENCES_VISIBILITY_NAME)]: () => {
    sendGAEvent<MarketingPreferencesImpression>({
      event: 'custom_event',
      event_category: 'Marketing preferences',
      event_action: 'Impression',
      event_label: 'Contact seller',
    });
  },
  [actions.onRelatedProductClick.type]: (prevState, _nextState, action) => {
    const { payload } = action as ReturnType<typeof actions.onRelatedProductClick>;
    const { placementStrategy = '', products }: PlacementEntry<RecommendedProduct> =
      prevState.richRelevance[Placement.SameSeller];

    if (products === null) {
      return;
    }

    const productIndex = products.findIndex(({ code }) => payload.code === code);
    const positionInList = productIndex + 1;
    const product = products[productIndex];
    const eventLabel = `${positionInList} : ${placementStrategy || ''} : ${Placement.SameSeller}`;

    sendGAEvent<InitialRelatedProductClickEvent>({
      ecommerce: null,
    });

    sendGAEvent<RelatedProductClick>({
      ...(payload.shouldUseEventCallback && {
        eventCallback: createEventCallbackRedirect(payload.href),
        eventTimeout: EVENT_TIMEOUT_IN_MS,
      }),
      event: 'product_clicks',
      event_action: 'Product Click',
      event_category: 'Rich Relevance',
      event_label: eventLabel,
      algonomy_tracking_URL: product.linkURL,
      ecommerce: {
        click: {
          actionField: {
            list: `product - rich relevance - ${Placement.SameSeller} - ${placementStrategy}`,
          },
          products: generateProductsForEvent(
            {
              products: [product],
              placementName: Placement.SameSeller,
              placementStrategy,
              positionInList,
            },
            ['list'],
          ),
        },
      },
    });
  },
};
